'use client';

import { usePathname } from 'next/navigation';
import { useState } from 'react';

import { useToast } from '@/components/ui/use-toast';
import { getLogger } from '@/logger/logger';
import { addItemsToCart } from '@/store/cart/cart.store';
import { CartItemBody } from '@/types/cart.types';
import { getCartOrigin } from '@/utils/cart/get-cart-origin.util';
import { getDuplicatedCartItemIds } from '@/utils/cart/get-duplicated-cart-items.util';

export const ADD_TO_CART_ERROR_MESSAGE = 'Não foi possível adicionar ao carrinho. Tente novamente.';
export const DUPLICATED_PROCEDURES_MESSAGE = 'Existem procedimentos duplicados no carrinho. Revise antes de prosseguir.';

const logger = getLogger('use-add-to-cart');

export interface AddToCartResult {
  wasAdded: boolean;
  eventInternalId?: string;
}

export interface UseAddToCartResult {
  addToCart: (items: CartItemBody[]) => Promise<AddToCartResult>;
  isAdding: boolean;
}

export function useAddToCart(): UseAddToCartResult {
  const pathname = usePathname();
  const { toast } = useToast();
  const [isAdding, setIsAdding] = useState(false);

  async function addToCart(items: CartItemBody[]): Promise<AddToCartResult> {
    setIsAdding(true);

    try {
      const { cart, addedItems, purchaseTrack } = await addItemsToCart(items, getCartOrigin(pathname));

      cart?.warnings?.forEach((warning) => toast.warn(warning));

      const duplicatedItemIds = getDuplicatedCartItemIds(cart?.items ?? []);
      const duplicatedAdditions = addedItems.filter((item) => duplicatedItemIds.has(item.id));

      if (duplicatedAdditions.length > 0) {
        logger.info('An addition left the patient holding the same procedure more than once', {
          duplicated: duplicatedAdditions.map(({ patient, procedure }) => ({
            patientId: patient.id,
            procedureId: procedure.id,
          })),
        });
        toast.warn(DUPLICATED_PROCEDURES_MESSAGE);
      }

      return { wasAdded: true, eventInternalId: purchaseTrack?.posthogEventInternalId };
    } catch (error) {
      logger.error('Failed to add items to the cart', {
        error,
        procedureIds: items.map((item) => item.procedureId),
        slotIds: items.map((item) => item.slotId),
      });
      toast.error(ADD_TO_CART_ERROR_MESSAGE);

      return { wasAdded: false };
    } finally {
      setIsAdding(false);
    }
  }

  return { addToCart, isAdding };
}
